/**
 * CreateWorkshopDrawer (BON-203) — guided 4-step Create Workshop flow over the
 * Workshops calendar. Built on the shared DS CreateDrawer primitive (same as
 * VenueCreateDrawer / BON-158); all state lives in useWorkshopCreateForm and
 * the step bodies in workshopCreateSteps. Creates the workshop as a DRAFT and
 * navigates to the new workshop's detail page on success.
 *
 * A repeating schedule creates the whole series of drafts and lands
 * on the first one. Workshop types are existing-only (no inline type creation).
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { RegionNode } from '../../../../api/regions';
import { fetchVenues } from '../../../../api/venues';
import { fetchWorkshopTypes, type WorkshopType } from '../../../../api/workshop-types';
import type { WorkshopDetail } from '../../../../api/workshops';
import {
  CreateDrawer,
  makeNavGuard,
  useDirtyClose,
  useStepAttention,
  type CreateDrawerStep,
} from '../../primitives';
import { DrawerScrim } from '../../primitives/DrawerScrim';
import { workshopDetailUrlFor, type WorkshopShell } from '../../workshopDetailUrlFor';

import { ReviewCta } from './ReviewCta';
import {
  useWorkshopCreateForm,
  type FieldMode,
  type WorkshopCreateForm,
} from './useWorkshopCreateForm';
import { regionNameById, selectableRegionForVenue, soleRegion } from './workshopCreateRegions';
import {
  LocationStep,
  ReviewStep,
  ScheduleStep,
  WorkshopStep,
  type VenueOption,
  type WorkshopTypeOption,
} from './workshopCreateSteps';
import { toVenueOptions } from './workshopCreateVenues';

export interface CreateWorkshopDrawerProps {
  /** Dashboards shell the drawer renders in — drives the post-create redirect. */
  shell: WorkshopShell;
  /** Seed for the Location step's Region field. */
  regionId?: string | null;
  /** Seed for the Location step's Venue field. */
  venueId?: string | null;
  /** Region field mode (round 8): `fixed` (text) | `prefill` (editable+seeded)
   *  | `open` (editable+empty). Default `open`. */
  regionMode?: FieldMode;
  /** Venue field mode — same semantics as `regionMode`. Default `open`. */
  venueMode?: FieldMode;
  /** Region label shown in the header eyebrow (e.g. the region's name). */
  regionLabel?: string;
  /** Regions the current user may write to, as a tree — the Region selector's
   *  options (used when the Region field is editable). */
  regions?: RegionNode[];
  /** True while `regions` is still being resolved, so the Location step can say
   *  so instead of presenting an empty list as the answer. */
  regionsLoading?: boolean;
  /** True when resolving `regions` FAILED, so the step can say the regions
   *  could not be loaded rather than blaming the viewer's permissions. */
  regionsFailed?: boolean;
  onClose: () => void;
}

const STALE_60S = 60_000;

/** Dirty-close confirmation copy (B3). */
const DISCARD_PROMPT = "Discard this workshop? Your changes won't be saved.";

/** Active workshop types → combobox options carrying their defaults. */
function toTypeOptions(types: WorkshopType[] | undefined): WorkshopTypeOption[] {
  return (types ?? [])
    .filter((t) => t.isActive)
    .map((t) => ({
      id: t.id,
      label: t.displayName || t.name,
      defaultCapacity: t.defaultCapacity,
      defaultPriceCents: t.defaultPriceCents,
      defaultDurationMinutes: t.defaultDurationMinutes,
    }));
}

interface StepArgs {
  form: WorkshopCreateForm;
  types: WorkshopTypeOption[];
  venues: VenueOption[];
  regions: RegionNode[];
  regionsLoading: boolean;
  regionsFailed: boolean;
  typeLabel: string;
  regionLabel: string;
  venueLabel: string;
}

/** The wizard steps — ALWAYS 4, every entry point: Location → Workshop →
 *  Schedule → Review. Location (Region + Venue) is always a real stop, even
 *  when both are pre-set. There is no Teacher step (BON-466): crew is assigned
 *  after the workshop is created and published. */
function buildSteps({
  form,
  types,
  venues,
  regions,
  regionsLoading,
  regionsFailed,
  typeLabel,
  regionLabel,
  venueLabel,
}: StepArgs): CreateDrawerStep[] {
  return [
    {
      id: 'location',
      label: 'Location',
      hint: 'region · venue',
      content: (
        <LocationStep
          form={form}
          regions={regions}
          regionsLoading={regionsLoading}
          regionsFailed={regionsFailed}
          venues={venues}
          regionLabel={regionLabel}
          venueLabel={venueLabel}
        />
      ),
    },
    {
      id: 'workshop',
      label: 'Workshop',
      hint: 'type · capacity · price',
      content: <WorkshopStep form={form} types={types} />,
    },
    {
      id: 'schedule',
      label: 'Schedule',
      hint: 'date · time',
      content: <ScheduleStep form={form} />,
    },
    {
      id: 'review',
      label: 'Review',
      hint: 'confirm · create',
      content: (
        <ReviewStep
          form={form}
          typeLabel={typeLabel}
          regionLabel={regionLabel}
          venueLabel={venueLabel}
        />
      ),
    },
  ];
}

/** The CTA and its foot note: one draft, or the whole series. */
function ctaCopy(form: WorkshopCreateForm): { ctaLabel: string; footNote: string } {
  const count = form.seriesPreview?.count ?? 0;
  if (count > 1) {
    return {
      ctaLabel: form.submitting ? 'Creating…' : `Create ${count} drafts`,
      footNote: `creates ${count} workshops in draft`,
    };
  }
  return {
    ctaLabel: form.submitting ? 'Creating…' : 'Create draft',
    footNote: 'creates the workshop in draft',
  };
}

/** Auto-select the region owning an already-seeded venue (Region left blank).
 *  The drawer can be launched from a page that knows the venue but not its
 *  region — `/admin/venues/:id` carries no region slug — and Region is required
 *  to leave the Location step; picking it by hand clears the venue seed, so the
 *  venue brings its own region instead. Runs as an effect because the
 *  venue→region mapping arrives with the venues query. */
function useAdoptVenueRegion(
  form: WorkshopCreateForm,
  venues: VenueOption[],
  regions: RegionNode[],
) {
  const { regionId, adoptVenueRegion } = form;
  const venueId = form.draft.schedule.venueId;
  useEffect(() => {
    if (regionId || !venueId) return;
    const owner = selectableRegionForVenue(venues, venueId, regions);
    if (owner) adoptVenueRegion(owner);
  }, [regionId, venueId, venues, regions, adoptVenueRegion]);
}

/** Select the viewer's only writable region for them. With one region there is
 *  no choice to make, and leaving it unselected would block the step behind a
 *  dropdown that could only ever be answered one way. Adopts rather than sets,
 *  so choosing it on the viewer's behalf does not mark the form dirty. */
function useAdoptSoleRegion(form: WorkshopCreateForm, regions: RegionNode[]) {
  const { regionId, regionMode, adoptVenueRegion } = form;
  const only = soleRegion(regions);
  const onlyId = only?.id;
  useEffect(() => {
    if (regionMode === 'fixed' || regionId || !onlyId) return;
    adoptVenueRegion(onlyId);
  }, [regionMode, regionId, onlyId, adoptVenueRegion]);
}

/** Load + shape the type/venue options and the review labels for the current
 *  region (extracted to keep the component under the line cap). */
function useCreateWorkshopData(
  form: WorkshopCreateForm,
  regions: RegionNode[],
  regionName?: string,
) {
  const typesQuery = useQuery({ queryKey: ['workshop-types'], queryFn: fetchWorkshopTypes });
  const venuesQuery = useQuery({
    queryKey: ['venues', 'all'],
    queryFn: fetchVenues,
    staleTime: STALE_60S,
  });

  const effectiveRegionId = form.regionId || null;

  const types = useMemo(() => toTypeOptions(typesQuery.data), [typesQuery.data]);
  const selectedVenueId = form.draft.schedule.venueId;
  const venues = useMemo(
    () => toVenueOptions(venuesQuery.data?.venues, effectiveRegionId, regions, selectedVenueId),
    [venuesQuery.data, effectiveRegionId, regions, selectedVenueId],
  );

  const typeLabel = types.find((t) => t.id === form.draft.workshop.workshopTypeId)?.label ?? '';
  const venueLabel = venues.find((v) => v.id === form.draft.schedule.venueId)?.name ?? '';
  // Prefer the explicit label prop (fixed flows pass the venue's region name);
  // otherwise resolve the selected region from the tree.
  const regionLabel = form.regionId
    ? regionNameById(regions, form.regionId) || regionName || ''
    : '';
  return { types, venues, typeLabel, regionLabel, venueLabel, effectiveRegionId };
}

export default function CreateWorkshopDrawer({
  shell,
  regionId = null,
  venueId = null,
  regionMode = 'open',
  venueMode = 'open',
  regionLabel,
  regions = [],
  regionsLoading = false,
  regionsFailed = false,
  onClose,
}: CreateWorkshopDrawerProps) {
  const navigate = useNavigate();
  const form = useWorkshopCreateForm(
    (created: WorkshopDetail) => {
      onClose();
      navigate(workshopDetailUrlFor(shell, created.id));
    },
    { regionId: regionId ?? undefined, venueId: venueId ?? undefined, regionMode, venueMode },
  );

  const requestClose = useDirtyClose(form.dirty, onClose, DISCARD_PROMPT);
  const { attend, announcement } = useStepAttention();
  const guard = makeNavGuard(form);
  const onNavigate = (target: number, current: number) => {
    const allowed = guard(target, current);
    if (!allowed) attend(`Step ${current + 1} of 4`);
    return allowed;
  };

  const data = useCreateWorkshopData(form, regions, regionLabel);
  const { types, venues, typeLabel, venueLabel } = data;
  useAdoptVenueRegion(form, venues, regions);
  useAdoptSoleRegion(form, regions);
  const steps = buildSteps({
    form,
    types,
    venues,
    regions,
    regionsLoading,
    regionsFailed,
    typeLabel,
    regionLabel: data.regionLabel,
    venueLabel,
  });

  const { ctaLabel, footNote } = ctaCopy(form);

  return (
    <DrawerScrim onScrimClick={requestClose}>
      <CreateDrawer
        title="New workshop"
        eyebrow={regionLabel ? `New workshop · ${regionLabel}` : 'New workshop'}
        quote="A workshop is a room, a date, and a reason to gather."
        steps={steps}
        step={form.step}
        onStepChange={form.setStep}
        ctaLabel={ctaLabel}
        continueAlwaysEnabled
        onNavigate={onNavigate}
        announcement={announcement}
        canContinue={!form.submitting}
        onCta={() => form.submit('navigate')}
        renderFinalCta={(canContinue) => (
          <ReviewCta form={form} label={ctaLabel} canContinue={canContinue} />
        )}
        onCancel={requestClose}
        onClose={requestClose}
        footNote={footNote}
        style={{ height: '100%' }}
      />
    </DrawerScrim>
  );
}
